import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, CircleAlert, LoaderCircle } from 'lucide-react';
import { usePushErrorText } from '@/push/push-error';
import { usePush } from '@/push/use-push';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const DISMISSED_KEY = 'nargus.push.dismissed';

export function PushPrompt() {
  const { t } = useTranslation();
  const errorText = usePushErrorText();
  const push = usePush();
  const [tried, setTried] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === '1',
  );

  if (dismissed || !push.ready || push.state !== 'off') return null;

  function dismiss() {
    localStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
  }

  function enable() {
    setTried(true);
    push.enable();
  }

  const problem = push.error
    ? errorText(push.error)
    : tried && !push.busy && push.permission !== 'granted'
      ? t('push.notGranted')
      : null;

  return (
    <Card className="rise mb-6 max-w-lg">
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Bell className="size-4" />
          </span>
          <div className="min-w-0">
            <p className="font-medium">{t('push.askTitle')}</p>
            <p className="text-sm text-muted-foreground">{t('push.askBody')}</p>
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button onClick={enable} disabled={push.busy}>
            {push.busy ? <LoaderCircle className="size-4 animate-spin" /> : <Bell />}
            {push.busy ? t('push.enabling') : t('push.enable')}
          </Button>
          <Button variant="ghost" onClick={dismiss} disabled={push.busy}>
            {t('push.later')}
          </Button>
        </div>

        {problem ? (
          <p className="flex items-start gap-1.5 text-sm text-destructive">
            <CircleAlert className="mt-0.5 size-4 shrink-0" />
            {problem}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
